/**
 * Visit Report — the structured record of what actually happened on a site visit.
 *
 * This replaces the AI-written "technical report" wall of text that used to be
 * the office's only account of a completed job. The office reads a visit by
 * looking at it, not by reading a paragraph a model wrote about it:
 *
 *   - which parts went in, which didn't, and what got added on the spot
 *   - how long the tech was on site vs what was booked
 *   - who went
 *   - the tech's notes, VERBATIM — wrap-up note + field log, unpadded
 *
 * See `VisitReportCard` for the surface. The AI's remaining jobs are narrow and
 * documented in `ai-summary.service.ts`: a customer-facing blurb for billing
 * correspondence (never shown on a job/deal), and a technical report for
 * Fault / Maintenance visits that kept a field log.
 *
 * Everything here is a PURE derivation — no Firestore reads, no writes. Feed it
 * a Job (preferred, always current) or one of the denormalised summaries that
 * were synced onto a deal / fault at completion time.
 */

import type {
  AdditionalGearItem,
  AdditionalGearReason,
  FieldLogEntry,
  Job,
  JobPhoto,
  Material,
} from '@/types/schema';
import { getOnSiteMinutes, resolveJobState } from './job-state.service';

// ─── Types ──────────────────────────────────────────────────────────────────

/**
 * What became of one part on this visit.
 *
 * Two of these exist to stop the card asserting something it doesn't know:
 *
 *  - `pending` — the visit hasn't finished. The kit is on the van; nobody has
 *    said anything about it yet. Never a verdict.
 *  - `unconfirmed` — the visit IS finished but the tech never ticked anything
 *    off (offline replay, backed out of the form, an older job). The completion
 *    form pre-ticks every planned item, so an empty `usedMaterials` on a closed
 *    job means the form was skipped, NOT that nothing went in. Calling those
 *    "not installed" would be a confident lie in the one place the office is
 *    trying to trust.
 */
export type VisitPartStatus =
  | 'installed'
  | 'not_installed'
  | 'extra'
  | 'unconfirmed'
  | 'pending';

export interface VisitPart {
  /** Stable key for React lists — material id, else the name. */
  key: string;
  name: string;
  status: VisitPartStatus;
  /** What the office sent the tech out with. Absent on `extra` rows. */
  plannedQty?: number;
  /** What the tech confirmed using. Absent on `not_installed` rows. */
  usedQty?: number;
  unit?: string;
  sku?: string;
  /** Adjustment reason, or the extra-gear reason + the tech's note. */
  note?: string;
  /**
   * Provenance — indexes into the `planned` / `used` / `extraGear` arrays this
   * row was derived from. Read-only surfaces ignore them; editors (the tech's
   * completion recap) use them to write a row change back to the right source
   * array without re-running the matching.
   */
  plannedIdx?: number;
  usedIdx?: number;
  gearIdx?: number;
}

export interface VisitPhoto {
  url: string;
  caption?: string;
  takenAt?: string;
  takenBy?: string;
  category?: string;
}

/** One raw note, exactly as the tech wrote it. Never summarised. */
export interface VisitLogEntry {
  at?: string;
  text: string;
  author?: string;
  entryType?: string;
}

export interface VisitReport {
  jobId?: string;
  jobType: string;
  /** "Visit 2 of 3", "Pre-Site", … — supplied by the caller that knows. */
  visitLabel?: string;
  /** YYYY-MM-DD the visit was scheduled for. */
  date?: string;
  completedAt?: string;

  techNames: string[];

  onSiteMinutes: number;
  bookedMinutes: number;
  /** True when the office hand-corrected the timer — worth showing. */
  onSiteCorrected: boolean;
  onSiteCorrectedBy?: string;

  /**
   * The tech has closed the visit out (COMPLETED or NEEDS_RETURN_VISIT).
   * Readers use this to decide whether the parts list is a VERDICT or just the
   * plan — the difference between "not installed" and "still on the van".
   */
  isFinished: boolean;

  parts: VisitPart[];
  partsPlanned: number;
  partsInstalled: number;
  partsNotInstalled: number;
  partsExtra: number;
  partsUnconfirmed: number;
  /** Planned kit on a visit that hasn't happened yet — not a verdict. */
  partsPending: number;

  photos: VisitPhoto[];

  /** The tech's wrap-up note, verbatim. */
  wrapUpNote?: string;
  /** The on-site field log, verbatim, oldest first. */
  fieldLog: VisitLogEntry[];

  needsReturnVisit: boolean;
  returnVisitReason?: string;

  /** SOS / flag reasons raised during the visit. */
  flags: string[];

  /**
   * AI technical report — present only for Fault / Maintenance visits that kept
   * a field log. Rendered as a secondary, collapsed section; the raw log above
   * it is the record.
   */
  aiTechnicalReport?: string;

  /** False when there is genuinely nothing to show — caller can skip the card. */
  hasContent: boolean;
}

// ─── Labels ─────────────────────────────────────────────────────────────────

/**
 * Why a tech grabbed something that wasn't on the provisioning list. Mirrors
 * `GEAR_REASON_OPTIONS` in JobCompletionFormModal, shortened for a chip.
 */
const GEAR_REASON_LABEL: Record<AdditionalGearReason, string> = {
  variation: 'Customer requested',
  replacement: 'Faulty on site',
  omission: 'Missed in quote',
  upgrade: 'Upgrade',
  other: 'Extra',
};

export function gearReasonLabel(reason?: string): string {
  if (!reason) return 'Extra';
  return GEAR_REASON_LABEL[reason as AdditionalGearReason] ?? reason;
}

export const PART_STATUS_LABEL: Record<VisitPartStatus, string> = {
  installed: 'Installed',
  not_installed: 'Not installed',
  extra: 'Added on site',
  unconfirmed: 'Not confirmed',
  pending: 'On the van',
};

// ─── Parts derivation ───────────────────────────────────────────────────────

/** Materials match on id when both have one, otherwise on a normalised name. */
function materialKey(m: { id?: string; name?: string }): string {
  return (m.id || '').trim() || (m.name || '').trim().toLowerCase();
}

/**
 * Exported for the completion recap's parts editor, which must predict how
 * THIS function will pair the arrays it writes — a planned row a tech flips
 * out can still be matched by a same-name tech-added entry.
 */
export function sameMaterial(a: Partial<Material>, b: Partial<Material>): boolean {
  if (a.id && b.id && a.id === b.id) return true;
  const an = (a.name || '').trim().toLowerCase();
  const bn = (b.name || '').trim().toLowerCase();
  return !!an && an === bn;
}

/**
 * Reconcile what was sent out against what came back confirmed.
 *
 * Three inputs, because all three carry different information:
 *   - `planned`   — what provisioning picked / what sales sold
 *   - `used`      — what the tech ticked off at completion (with real quantities)
 *   - `extraGear` — what the tech grabbed that was never on the list
 *
 * A planned item the tech un-ticked comes back `not_installed`, which is the
 * single most useful thing on this card: it means a part is still in the van
 * and probably shouldn't be billed.
 */
export function reconcileParts(
  planned: Partial<Material>[],
  used: Partial<Material>[],
  extraGear: AdditionalGearItem[] = [],
  opts: { confirmed?: boolean; finished?: boolean } = {},
): VisitPart[] {
  const parts: VisitPart[] = [];
  const matchedUsed = new Set<number>();

  // What an un-matched planned item means depends on whether the visit has
  // happened. Before it has, the kit is simply on the van (`pending`); after it
  // has, either the tech left it off (`not_installed`) or never ticked anything
  // at all (`unconfirmed`). Defaults keep the bare 3-arg call unchanged.
  const finished = opts.finished !== false;
  const missStatus: VisitPartStatus = !finished
    ? 'pending'
    : opts.confirmed === false
      ? 'unconfirmed'
      : 'not_installed';

  planned.forEach((p, pi) => {
    const idx = used.findIndex((u, i) => !matchedUsed.has(i) && sameMaterial(p, u));
    const u = idx >= 0 ? used[idx] : undefined;
    if (idx >= 0) matchedUsed.add(idx);

    if (u) {
      parts.push({
        key: materialKey(p) || `planned-${parts.length}`,
        name: p.name ?? 'Item',
        status: 'installed',
        plannedQty: p.quantity,
        usedQty: u.actualQuantity ?? u.quantity ?? p.quantity,
        unit: p.unit ?? u.unit,
        sku: p.sku ?? u.sku,
        plannedIdx: pi,
        usedIdx: idx,
        ...(u.adjustmentReason ? { note: u.adjustmentReason } : {}),
      });
    } else {
      parts.push({
        key: materialKey(p) || `planned-${parts.length}`,
        name: p.name ?? 'Item',
        status: missStatus,
        plannedQty: p.quantity,
        unit: p.unit,
        sku: p.sku,
        plannedIdx: pi,
      });
    }
  });

  // Confirmed items with no planned counterpart — the tech added them to the
  // materials list itself (addedByTech), rather than via the extra-gear form.
  used.forEach((u, i) => {
    if (matchedUsed.has(i)) return;
    parts.push({
      key: materialKey(u) || `used-${i}`,
      name: u.name ?? 'Item',
      status: 'extra',
      usedQty: u.actualQuantity ?? u.quantity ?? 0,
      unit: u.unit,
      sku: u.sku,
      usedIdx: i,
      ...(u.adjustmentReason ? { note: u.adjustmentReason } : {}),
    });
  });

  // Extra gear captured through the completion form's own "additional gear" row.
  extraGear.forEach((g, i) => {
    const note = [gearReasonLabel(g.reason), g.notes?.trim()].filter(Boolean).join(' — ');
    parts.push({
      key: `gear-${g.name}-${i}`,
      name: g.name,
      status: 'extra',
      usedQty: g.quantity,
      gearIdx: i,
      ...(note ? { note } : {}),
    });
  });

  return parts;
}

function tallyParts(parts: VisitPart[]) {
  return {
    partsPlanned: parts.filter((p) => p.status !== 'extra').length,
    partsInstalled: parts.filter((p) => p.status === 'installed').length,
    partsNotInstalled: parts.filter((p) => p.status === 'not_installed').length,
    partsExtra: parts.filter((p) => p.status === 'extra').length,
    partsUnconfirmed: parts.filter((p) => p.status === 'unconfirmed').length,
    partsPending: parts.filter((p) => p.status === 'pending').length,
  };
}

// ─── Adapters ───────────────────────────────────────────────────────────────

function normaliseLog(entries: Pick<FieldLogEntry, 'timestamp' | 'text' | 'techName' | 'entryType'>[]): VisitLogEntry[] {
  return [...entries]
    .filter((e) => !!e?.text?.trim())
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    .map((e) => ({
      at: e.timestamp,
      text: e.text.trim(),
      ...(e.techName ? { author: e.techName } : {}),
      ...(e.entryType ? { entryType: e.entryType } : {}),
    }));
}

function normalisePhotos(photos: JobPhoto[]): VisitPhoto[] {
  return photos.map((p) => ({
    url: p.url,
    ...(p.caption ? { caption: p.caption } : {}),
    ...(p.timestamp ? { takenAt: p.timestamp } : {}),
    ...(p.techName || p.techId ? { takenBy: p.techName || p.techId } : {}),
    ...(p.category ? { category: p.category } : {}),
  }));
}

function resolveTechNames(job: Job, techMap?: Record<string, string>): string[] {
  const ids = job.techIds && job.techIds.length > 0 ? job.techIds : job.techId ? [job.techId] : [];
  const named = ids.map((id) => techMap?.[id]).filter((n): n is string => !!n);
  if (named.length > 0) return named;
  // Denormalised fallbacks — set on some jobs, and the only source for a job
  // whose tech has since left the team and dropped out of `techMap`.
  const denorm = (job as { techName?: string }).techName;
  if (denorm) return [denorm];
  return [];
}

/**
 * Build a visit report straight from the live Job document.
 *
 * Prefer this everywhere the Job is loadable — it is always current, and it
 * carries `plannedMaterials`, which the denormalised deal/fault summaries do
 * not, so it's the only source that can say a part was NOT installed.
 */
export function visitReportFromJob(
  job: Job,
  opts: { techMap?: Record<string, string>; visitLabel?: string; assumeFinished?: boolean } = {},
): VisitReport {
  const planned = job.plannedMaterials ?? [];
  const used = job.usedMaterials ?? [];
  const extraGear = job.additionalGearUsed ?? [];

  // Only a closed-out visit can produce a verdict on the kit. Before that the
  // planned rows are just "on the van". `assumeFinished` is for the completion
  // recap: the job doc isn't COMPLETED yet while the tech reviews it, but the
  // whole point of that screen is to preview the post-completion verdict — so
  // an unticked part must read "not installed", not "on the van".
  const state = resolveJobState(job);
  const finished =
    opts.assumeFinished === true || state === 'COMPLETED' || state === 'NEEDS_RETURN_VISIT';
  // "Confirmed" = the tech actually went through the gear list.
  //
  // `materialsConfirmedAt` is the explicit signal and wins outright: it's the
  // only way to express "the tech looked and NOTHING went in", which the
  // inference below reads as a skipped form. Jobs completed before that stamp
  // existed fall back to it — the form pre-ticks every planned item, so an
  // empty usedMaterials there really does mean the form was skipped.
  const confirmed =
    !!job.materialsConfirmedAt || used.length > 0 || extraGear.length > 0 || planned.length === 0;
  const parts = reconcileParts(planned, used, extraGear, { confirmed, finished });

  const fieldLog = normaliseLog(job.fieldLog ?? []);
  const photos = normalisePhotos(job.photos ?? []);
  const wrapUpNote = job.completionNote?.trim() || undefined;

  const flags: string[] = [];
  if (job.sos && job.sosReason) flags.push(job.sosReason);
  if (job.flagged && job.flagReason) flags.push(job.flagReason);

  return {
    jobId: job.id,
    jobType: job.type || 'Job',
    visitLabel: opts.visitLabel,
    date: job.date,
    completedAt: job.completedAt || job.onsiteEndedAt,

    techNames: resolveTechNames(job, opts.techMap),

    onSiteMinutes: getOnSiteMinutes(job),
    bookedMinutes: job.duration || 0,
    onSiteCorrected: !!job.onSiteMinutesCorrectedAt,
    onSiteCorrectedBy: job.onSiteMinutesCorrectedByName,
    isFinished: finished,

    parts,
    ...tallyParts(parts),

    photos,
    wrapUpNote,
    fieldLog,

    needsReturnVisit: !!job.needsReturnVisit,
    returnVisitReason: job.returnVisitReason,
    flags,

    aiTechnicalReport: job.aiSummary?.technicalReport?.trim() || undefined,

    hasContent:
      parts.length > 0 ||
      photos.length > 0 ||
      fieldLog.length > 0 ||
      !!wrapUpNote ||
      flags.length > 0,
  };
}

/**
 * Shape of the per-visit blob synced onto `deal.installSummaries[jobId]` at
 * install completion (`syncInstallCompletionToDeal`). Structurally identical to
 * the legacy single `deal.installSummary`.
 */
export interface InstallSummaryLike {
  jobId?: string;
  techName?: string;
  duration?: number;
  completedAt?: string;
  completionNote?: string;
  technicalReport?: string;
  photos?: VisitPhoto[];
  /**
   * Looser than `Material` on purpose. The sync writes a hand-picked subset
   * (name / quantity / unit / sku / addedByTech) with no `id`, so requiring the
   * full Material shape here would reject the very data this adapter exists to
   * read.
   */
  usedMaterials?: Partial<Material>[];
  additionalGearUsed?: { what?: string; name?: string; qty?: number; quantity?: number; reason?: string; notes?: string }[];
  fieldLogCount?: number;
  photoCount?: number;
}

/**
 * Fallback adapter for a deal whose Job document isn't loaded (or was deleted).
 *
 * The denormalised summary carries no `plannedMaterials`, so every part it
 * knows about was confirmed used — it can never report "not installed". That's
 * a real gap, not something to paper over: prefer `visitReportFromJob` and only
 * fall back here when there's no Job to read.
 */
export function visitReportFromInstallSummary(summary: InstallSummaryLike): VisitReport {
  const used = summary.usedMaterials ?? [];
  const extraGear: AdditionalGearItem[] = (summary.additionalGearUsed ?? []).map((g) => ({
    name: g.name || g.what || 'Extra item',
    quantity: g.quantity ?? g.qty ?? 1,
    reason: (g.reason as AdditionalGearReason) || 'other',
    ...(g.notes ? { notes: g.notes } : {}),
  }));
  const parts = reconcileParts([], used, extraGear);
  const photos = summary.photos ?? [];
  const wrapUpNote = summary.completionNote?.trim() || undefined;

  return {
    jobId: summary.jobId,
    jobType: 'Install',
    completedAt: summary.completedAt,
    techNames: summary.techName ? [summary.techName] : [],
    onSiteMinutes: summary.duration ?? 0,
    bookedMinutes: 0,
    onSiteCorrected: false,
    isFinished: true,
    parts,
    ...tallyParts(parts),
    photos,
    wrapUpNote,
    fieldLog: [],
    needsReturnVisit: false,
    flags: [],
    aiTechnicalReport: summary.technicalReport?.trim() || undefined,
    hasContent: parts.length > 0 || photos.length > 0 || !!wrapUpNote,
  };
}

/**
 * Shape of `fault.deploymentJobSummary` — what a completed Fault job syncs back
 * onto the fault record. Unlike the install summary this one DOES carry the
 * full field log, which is the whole point for a fault.
 */
export interface DeploymentSummaryLike {
  techName?: string;
  actualDuration?: number;
  completionNote?: string;
  fieldLogEntries?: { timestamp: string; text: string; techName?: string; entryType?: string }[];
  photos?: VisitPhoto[];
  usedMaterials?: Partial<Material>[];
  additionalGearUsed?: AdditionalGearItem[];
  needsReturnVisit?: boolean;
  returnVisitReason?: string;
  technicalReport?: string;
}

/**
 * Adapter for the fault record's deployment summary.
 *
 * Faults rarely carry planned materials, so parts here are "what the tech used"
 * rather than a plan-vs-actual reconciliation — the field log is the substance.
 */
export function visitReportFromDeploymentSummary(summary: DeploymentSummaryLike): VisitReport {
  const parts = reconcileParts([], summary.usedMaterials ?? [], summary.additionalGearUsed ?? []);
  const fieldLog = normaliseLog(
    (summary.fieldLogEntries ?? []).map((e) => ({
      timestamp: e.timestamp,
      text: e.text,
      techName: e.techName,
      entryType: e.entryType as FieldLogEntry['entryType'],
    })),
  );
  const photos = summary.photos ?? [];
  const wrapUpNote = summary.completionNote?.trim() || undefined;

  return {
    jobType: 'Fault',
    techNames: summary.techName ? [summary.techName] : [],
    onSiteMinutes: summary.actualDuration ?? 0,
    bookedMinutes: 0,
    onSiteCorrected: false,
    isFinished: true,
    parts,
    ...tallyParts(parts),
    photos,
    wrapUpNote,
    fieldLog,
    needsReturnVisit: !!summary.needsReturnVisit,
    returnVisitReason: summary.returnVisitReason,
    flags: [],
    aiTechnicalReport: summary.technicalReport?.trim() || undefined,
    hasContent:
      parts.length > 0 || photos.length > 0 || fieldLog.length > 0 || !!wrapUpNote,
  };
}
